import { z } from 'zod';

/**
 * Zod contract for the Create Practice modal (`CreatePracticeModal`).
 * Wired with Mantine `useForm` + `zod4Resolver(createPracticeFormSchema)` (Zod v4).
 *
 * Custom field rules (phone, EIN, NPI, ZIP, city characters) live in
 * {@link createPracticeFormSchema}'s `.superRefine()` with explicit `path`
 * entries so Zod 4 + mantine-form-zod-resolver attach errors to the right
 * inputs (Zod string-level `.refine()` can emit `path: []`, which Mantine
 * does not map to a field).
 */

function digitsOnly(s: string): string {
  return s.replace(/\D/g, '');
}

/** Empty string OK; otherwise a plausible email shape. */
const optionalEmail = z.union([
  z.literal(''),
  z.string().trim().email('Enter a valid email address'),
]);

const US_PHONE_MSG = 'Enter a valid US phone number (10 digits, or 11 starting with 1)';

function isValidOptionalUsPhone(s: string): boolean {
  if (!s.trim()) return true;
  const d = digitsOnly(s);
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
}

export const createPracticeFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Practice name is required').max(200, 'Practice name is too long'),
    legalName: z.string().trim().max(200, 'Legal name is too long'),
    contactEmail: optionalEmail,
    phone: z.string().trim(),
    ein: z.string().trim(),
    npi: z.string().trim(),
    addressLine1: z.string().trim().max(200, 'Address line 1 is too long'),
    addressLine2: z.string().trim().max(200, 'Address line 2 is too long'),
    city: z.string().trim().max(100, 'City is too long'),
    state: z.string().trim().max(2, 'Use a 2-letter state code'),
    postalCode: z.string().trim(),
    ownerFirstName: z.string().trim().min(1, 'Owner first name is required').max(80, 'First name is too long'),
    ownerLastName: z.string().trim().min(1, 'Owner last name is required').max(80, 'Last name is too long'),
    ownerEmail: z
      .string()
      .trim()
      .min(1, 'Owner email is required')
      .email('Enter a valid email address'),
    ownerPhone: z.string().trim(),
  })
  .superRefine((data, ctx) => {
    if (data.phone && !isValidOptionalUsPhone(data.phone)) {
      ctx.addIssue({
        code: 'custom',
        message: US_PHONE_MSG,
        path: ['phone'],
        input: data.phone,
      });
    }
    if (data.ownerPhone && !isValidOptionalUsPhone(data.ownerPhone)) {
      ctx.addIssue({
        code: 'custom',
        message: US_PHONE_MSG,
        path: ['ownerPhone'],
        input: data.ownerPhone,
      });
    }

    const ein = data.ein.trim();
    if (ein && digitsOnly(ein).length !== 9) {
      ctx.addIssue({
        code: 'custom',
        message: 'EIN must be 9 digits (e.g. 12-3456789)',
        path: ['ein'],
        input: data.ein,
      });
    }

    const npi = data.npi.trim();
    if (npi && !/^\d{10}$/.test(digitsOnly(npi))) {
      ctx.addIssue({
        code: 'custom',
        message: 'NPI must be exactly 10 digits',
        path: ['npi'],
        input: data.npi,
      });
    }

    const postal = data.postalCode.trim();
    if (postal && !/^\d{5}(-\d{4})?$/.test(postal)) {
      ctx.addIssue({
        code: 'custom',
        message: 'ZIP must be 5 digits or ZIP+4 (e.g. 94612 or 94612-1234)',
        path: ['postalCode'],
        input: data.postalCode,
      });
    }

    const city = data.city.trim();
    if (city && !/^[\p{L}0-9\s\-'.]+$/u.test(city)) {
      ctx.addIssue({
        code: 'custom',
        message: 'City contains invalid characters',
        path: ['city'],
        input: data.city,
      });
    }
  });

export type CreatePracticeFormValues = z.infer<typeof createPracticeFormSchema>;

/** User-facing labels for Create Practice form fields (Zod paths / Mantine keys). */
export const CREATE_PRACTICE_FIELD_LABELS: Record<keyof CreatePracticeFormValues, string> = {
  name: 'Practice name',
  legalName: 'Legal name',
  contactEmail: 'Contact email',
  phone: 'Practice phone',
  ein: 'EIN',
  npi: 'NPI',
  addressLine1: 'Address line 1',
  addressLine2: 'Address line 2',
  city: 'City',
  state: 'State',
  postalCode: 'ZIP code',
  ownerFirstName: 'Owner first name',
  ownerLastName: 'Owner last name',
  ownerEmail: 'Owner email',
  ownerPhone: 'Owner phone',
};

const CREATE_PRACTICE_FIELD_ORDER = Object.keys(
  CREATE_PRACTICE_FIELD_LABELS,
) as (keyof CreatePracticeFormValues)[];

/** Human-readable lines for every Zod issue (covers all schema fields, including unions/refines). */
export function getCreatePracticeValidationLines(values: unknown): string[] {
  const parsed = createPracticeFormSchema.safeParse(values);
  if (parsed.success) return [];

  const byField = new Map<string, Set<string>>();
  for (const issue of parsed.error.issues) {
    const top = issue.path[0];
    const fieldKey =
      typeof top === 'string'
        ? top
        : top !== undefined
          ? String(top)
          : '_root';
    if (!byField.has(fieldKey)) byField.set(fieldKey, new Set());
    byField.get(fieldKey)!.add(issue.message);
  }

  const order = CREATE_PRACTICE_FIELD_ORDER;

  const lines: string[] = [];
  const emitted = new Set<string>();

  const pushField = (fieldKey: string): void => {
    const msgs = byField.get(fieldKey);
    if (!msgs?.size) return;
    emitted.add(fieldKey);
    const label =
      fieldKey === '_root'
        ? 'Form'
        : CREATE_PRACTICE_FIELD_LABELS[fieldKey as keyof CreatePracticeFormValues] ?? fieldKey;
    lines.push(`${label}: ${[...msgs].join('; ')}`);
  };

  for (const key of order) {
    pushField(key as string);
  }
  pushField('_root');

  for (const fieldKey of byField.keys()) {
    if (!emitted.has(fieldKey)) {
      pushField(fieldKey);
    }
  }

  return lines.length > 0 ? lines : ['Please review the highlighted fields above.'];
}

/** Mantine `initialValues` — keys must match `createPracticeFormSchema` (compile-time enforced). */
export const createPracticeFormInitialValues: CreatePracticeFormValues = {
  name: '',
  legalName: '',
  contactEmail: '',
  phone: '',
  ein: '',
  npi: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  ownerFirstName: '',
  ownerLastName: '',
  ownerEmail: '',
  ownerPhone: '',
};
